import React, { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  FormGroup,
  RadioGroup,
  Radio,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  FormControl,
  FormLabel,
  Stack,
  Tooltip,
  CircularProgress,
  SelectChangeEvent,
  OutlinedInput,
  ListItemText,
  FormHelperText,
  Link,
} from "@mui/material";
import {
  previousStep,
  changeData,
  nextStep,
  useSteps,
} from "../../shared/features/subscription/stepsSlice";
import { useDispatch, useSelector } from "react-redux";
import { FormGrid } from "components/FormGrid";
import { IFormQuery, SubscribeData } from "shared/interfaces";
import { yupResolver } from "@hookform/resolvers/yup";
import { stepTwoSchema } from "shared/schemas/stepTwo";
import { toast } from "react-toastify";
import GitHubIcon from "@mui/icons-material/GitHub";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Error } from "../TextError/index";
import { ArrowCircleRight } from "@mui/icons-material";
import { FormTitleSession } from "components/FormTitleSession";

const names = [
  "C#",
  "Javascript",
  "Kotlin",
  "Java",
  "C++",
  "PHP",
  "Ruby",
  "Python",
  "Swift",
  "C",
  "Outras",
];

// Lista de novas trilhas de TI estáticas
const trilhas = [
  { value: "FRONTEND", nome: "Desenvolvimento Front-end" },
  { value: "BACKEND", nome: "Desenvolvimento Back-end" },
  { value: "FULLSTACK", nome: "Desenvolvimento Full Stack" },
  { value: "MOBILE", nome: "Desenvolvimento Mobile" },
  { value: "QA", nome: "Garantia de Qualidade & Testes" },
  { value: "DEVOPS", nome: "DevOps" },
  { value: "DADOS", nome: "Engenharia de Dados" },
  { value: "SEGURANCA", nome: "Segurança da Informação" },
  { value: "PRODUCT_MANAGEMENT", nome: "Gestão de Produtos de Tecnologia (Product Management)" },
  { value: "UI_UX", nome: "Design de Experiência e Interface (UI/UX Design)" },
  { value: "OUTRA", nome: "Outra área de TI" },
];

// Função para converter todos os campos booleanos de "T"/"F" para true/false
function normalizeBooleans(data: any) {
  const booleanFields = [
    "matriculado",
    "matriculadoBoolean",
    "disponibilidadeBoolean",
    "efetivacaoBoolean",
    "lgpdBoolean",
    "polPrivacidade",
    "provaBoolean",
  ];
  const normalized = { ...data };
  booleanFields.forEach((field) => {
    if (typeof normalized[field] === "string") {
      normalized[field] =
        normalized[field] === "T"
          ? true
          : normalized[field] === "F"
          ? false
          : normalized[field];
    }
  });
  return normalized;
}

export const StepTwo: React.FC = () => {
  const dispatch = useDispatch();
  const [focusedField, setFocusedField] = useState<string | null>(null);

  // Salva dados no sessionStorage ignorando arquivos e filtros fora do escopo
  const saveToSession = (data: Partial<SubscribeData>, langs: string[]) => {
    const allowedFields = [
      "trilha1",
      "trilha2",
      "matriculado",
      "polPrivacidade",
      "instituicao",
      "curso",
      "qtdSemestres",
      "semestreAtual",
      "provaBoolean",
      "algoimportante",
      "resposta",
      "disponibilidadeBoolean",
      "efetivacaoBoolean",
      "github",
      "linkedin",
      "descConfigPc",
      "lgpdBoolean",
      "linguagens",
      "tipoGraduacao",
      "turno",
      "comoSoube",
      "espanhol",
      "ingles",
    ];

    const fixed = {
      ...data,
      linguagens: Array.isArray(langs)
        ? langs.filter((l): l is string => typeof l === "string")
        : langs,
    };
    const normalized = normalizeBooleans(fixed);
    const { curriculo, comprovanteMatricula, ...toPersist } = normalized;
    const filtered = Object.fromEntries(
      Object.entries(toPersist).filter(([key]) => allowedFields.includes(key))
    );
    sessionStorage.setItem("stepTwoFormData", JSON.stringify(filtered));
  };

  const getSessionData = (): Partial<SubscribeData> => {
    const raw = sessionStorage.getItem("stepTwoFormData");
    return raw ? JSON.parse(raw) : {};
  };

  const { data: formData } = useSelector(useSteps);
  const initialSession = getSessionData();
  const [languages, setLanguage] = React.useState<string[]>(
    initialSession.linguagens || formData?.linguagens || []
  );
  const [hasLang, setHasLang] = useState<boolean>(false);

  const handleChange = (event: SelectChangeEvent<typeof languages>) => {
    const {
      target: { value },
    } = event;
    const langs = typeof value === "string" ? value.split(",") : value;
    setLanguage(langs);
    saveToSession(watch(), langs);
  };

  useEffect(() => {
    setHasLang(languages.length === 0);
  }, [languages]);

  useEffect(() => {
    setHasLang(false);
    window.scrollTo(0, 0);
  }, []);

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<SubscribeData>({
    defaultValues: {
      ...initialSession,
      trilha1: initialSession.trilha1 || "",
      trilha2: initialSession.trilha2 || "",
      matriculado: initialSession.matriculado || "T",
      polPrivacidade: initialSession.polPrivacidade || false,
      ingles: initialSession.ingles || "NENHUM",
      espanhol: initialSession.espanhol || "NENHUM",
      lgpdBoolean: initialSession.lgpdBoolean ?? false,
    },
    resolver: yupResolver(stepTwoSchema),
  });

  const handleSelectChange =
    (field: keyof SubscribeData) => (event: SelectChangeEvent) => {
      const value = event.target.value;
      setValue(field, value, { shouldValidate: true });
      const current = watch();
      const updated = { ...current, [field]: value };
      saveToSession(updated, languages);
    };

  useEffect(() => {
    const subscription = watch((value) => {
      const { curriculo, comprovanteMatricula, ...rest } = value;
      const fixedValue = {
        ...rest,
        linguagens: Array.isArray(languages)
          ? languages.filter((l): l is string => typeof l === "string")
          : languages,
      };
      saveToSession(fixedValue, fixedValue.linguagens as string[]);
    });
    return () => subscription.unsubscribe();
  }, [watch, languages]);

  const verificar = () => {
    setHasLang(languages.length === 0);
  };

  const validateReadableFile = async (file: any, fieldLabel: string) => {
    if (!(file instanceof File)) {
      return `Não foi possível localizar o arquivo de ${fieldLabel}. Anexe novamente antes de enviar.`;
    }

    if (file.size <= 0) {
      return `O arquivo de ${fieldLabel} está vazio. Anexe um arquivo válido para continuar.`;
    }

    try {
      const buffer = await file.arrayBuffer();
      if (buffer.byteLength <= 0) {
        return `Não foi possível ler o arquivo de ${fieldLabel}. Anexe novamente antes de enviar.`;
      }
    } catch {
      return `Não foi possível acessar o arquivo de ${fieldLabel}. Anexe novamente antes de enviar.`;
    }

    return null;
  };

  const validateFilesBeforeNextStep = async (form: SubscribeData) => {
    const curriculoFile = form.curriculo?.[0];
    const comprovanteFile = form.comprovanteMatricula?.[0];

    const [curriculoError, comprovanteError] = await Promise.all([
      validateReadableFile(curriculoFile, "currículo"),
      validateReadableFile(comprovanteFile, "comprovante de matrícula"),
    ]);

    if (curriculoError) {
      setError("curriculo", { type: "manual", message: curriculoError });
    } else {
      clearErrors("curriculo");
    }

    if (comprovanteError) {
      setError("comprovanteMatricula", {
        type: "manual",
        message: comprovanteError,
      });
    } else {
      clearErrors("comprovanteMatricula");
    }

    if (curriculoError || comprovanteError) {
      const firstErrorMessage = curriculoError || comprovanteError;
      if (firstErrorMessage) {
        toast.error(firstErrorMessage);
      }

      const targetId = curriculoError
        ? "label-curriculo-candidato"
        : "label-comprovante-matricula";
      const target = document.getElementById(targetId);
      target?.scrollIntoView({ behavior: "smooth", block: "center" });

      return false;
    }

    return true;
  };

  const onSubmit = async (data: SubscribeData) => {
    data = normalizeBooleans(data);
    data.algoimportante = data.algoimportante.replace(/(\r\n|\n|\r)/gm, "");
    data.resposta = data.resposta.replace(/(\r\n|\n|\r)/gm, "");

    if (languages.length === 0) {
      setHasLang(true);
      const selecao: HTMLElement | null = document.getElementById(
        "s2-select-linguagens-checkbox"
      );
      selecao?.scrollIntoView();
      return;
    }

    const filesValid = await validateFilesBeforeNextStep(data);
    if (!filesValid) return;

    if (data.resposta === "") data.resposta = "Nenhuma";
    saveToSession(data, languages);

    dispatch(nextStep());
    dispatch(
      changeData({
        ...data,
        linguagens: languages,
        trilha1: data.trilha1,
        trilha2: data.trilha2,
        turno: data.turno,
      })
    );
  };

  const onInvalidSubmit = (formErrors: any) => {
    const comprovanteError = formErrors?.comprovanteMatricula?.message;
    const curriculoError = formErrors?.curriculo?.message;
    const fileErrorMessage = comprovanteError || curriculoError;

    if (typeof fileErrorMessage === "string" && fileErrorMessage.trim()) {
      toast.error(fileErrorMessage);
    }
  };

  const FormName: React.FC<IFormQuery> = ({ nome }) => {
    return <>{nome ? nome : <CircularProgress size={22} />}</>;
  };

  const helperWithCount = (
    value: string | undefined,
    max: number,
    error?: string,
    fieldName?: string
  ) => {
    const count = (value ?? "").length;
    const showCount = focusedField === fieldName;

    if (!error && !showCount) return undefined;

    return (
      <Box
        component="span"
        sx={{
          display: "flex",
          width: "100%",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box component="span">{error ?? ""}</Box>
        {showCount && (
          <Box component="span" sx={{ ml: "auto", textAlign: "right" }}>
            {`${count}/${max}`}
          </Box>
        )}
      </Box>
    );
  };

  const matriculado = watch("matriculado");
  const curriculo = watch("curriculo");
  const comprovanteMatricula = watch("comprovanteMatricula");
  const instituicaoValue = watch("instituicao") ?? "";
  const cursoValue = watch("curso") ?? "";
  const algoImportanteValue = watch("algoimportante") ?? "";
  const respostaValue = watch("resposta") ?? "";
  const githubValue = watch("github") ?? "";
  const linkedinValue = watch("linkedin") ?? "";
  const descConfigPcValue = watch("descConfigPc") ?? "";
  const semestresTotais = watch("qtdSemestres") as unknown as number | undefined;
  const semestreAtual = watch("semestreAtual") as unknown as number | undefined;
  const isGreaterThanTotalSemesters =
    (semestreAtual ?? 0) > (semestresTotais ?? Number.MAX_SAFE_INTEGER);

  return (
    <FormGrid onSubmit={handleSubmit(onSubmit, onInvalidSubmit)}>
      <FormTitleSession title={"Dados Acadêmicos"} />
      <Grid item xs={12} lg={12}>
        <FormLabel sx={{ color: "#525252" }}>
          <FormName nome={"Matriculado em Instituição de Ensino Superior ou Técnico. *"} />
        </FormLabel>
        <RadioGroup row sx={{ color: "#525252" }} defaultValue="T">
          <FormControlLabel
            id="matriculado-sim"
            value="T"
            control={<Radio />}
            label="Sim"
            {...register("matriculado")}
          />
          <FormControlLabel
            id="matriculado-nao"
            value="F"
            control={<Radio />}
            label="Não"
            {...register("matriculado")}
          />
        </RadioGroup>
      </Grid>

      {matriculado === "T" && (
        <>
          <Grid item xs={12} lg={6}>
            <TextField
              fullWidth
              sx={{ "&::placeholder": { color: "#525252" } }}
              id="instituicao-de-ensino-candidato"
              defaultValue={formData?.instituicao}
              label={<FormName nome={"Instituição de Ensino *"} />}
              error={!!errors.instituicao}
              helperText={helperWithCount(instituicaoValue, 100, errors.instituicao?.message, "instituicao")}
              inputProps={{ maxLength: 100 }}
              {...register("instituicao")}
              onFocus={() => setFocusedField("instituicao")}
              onBlur={() => setFocusedField(null)}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <TextField
              fullWidth
              id="curso-candidato"
              defaultValue={formData?.curso}
              label={<FormName nome={"Curso vinculado a sua instituição de ensino *"} />}
              error={!!errors.curso}
              helperText={helperWithCount(cursoValue, 50, errors.curso?.message, "curso")}
              inputProps={{ maxLength: 50 }}
              {...register("curso")}
              onFocus={() => setFocusedField("curso")}
              onBlur={() => setFocusedField(null)}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <FormControl fullWidth error={!!errors.tipoGraduacao}>
              <InputLabel sx={{ color: "#525252" }} id="tipo-graduacao-label">
                <FormName nome={"Selecione o tipo de graduação do seu curso *"} />
              </InputLabel>
              <Select
                id="s2-tipo-graduacao"
                labelId="tipo-graduacao-label"
                label={<FormName nome={"Selecione o tipo de graduação do seu curso *"} />}
                value={watch("tipoGraduacao") ?? initialSession.tipoGraduacao ?? ""}
                onChange={handleSelectChange("tipoGraduacao")}
                error={!!errors.tipoGraduacao}
              >
                <MenuItem value="TECNICO">Técnico</MenuItem>
                <MenuItem value="TECNOLOGO">Tecnólogo ou Tecnológico</MenuItem>
                <MenuItem value="BACHARELADO">Bacharelado</MenuItem>
              </Select>
              {errors.tipoGraduacao && (
                <FormHelperText>{errors.tipoGraduacao.message}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} lg={6}>
            <Tooltip
              title="Para estudantes EAD, que não possuam aulas síncronas na MANHÃ e TARDE, marcar a opção de turno 'NOITE'."
              placement="top"
              arrow
            >
              <FormControl fullWidth error={!!errors.turno}>
                <InputLabel sx={{ color: "#525252" }}>
                  <FormName nome={"Selecione o turno da sua graduação *"} />
                </InputLabel>

                <Select
                  id="s2-turno"
                  label={<FormName nome={"Selecione o turno da sua graduação *"} />}
                  value={watch("turno") ?? initialSession.turno ?? ""}
                  onChange={handleSelectChange("turno")}
                  error={!!errors.turno}
                >
                  <MenuItem value="NOITE">Noite</MenuItem>
                  <MenuItem value="TARDE">Tarde</MenuItem>
                  <MenuItem value="MANHA">Manhã</MenuItem>
                </Select>
              </FormControl>
            </Tooltip>
            <Error id={"mensagem-erro-campo-matriculado"} width={"100%"}>
              {errors.turno?.message}
            </Error>
          </Grid>
          <Grid item xs={12} lg={6}>
            <TextField
              fullWidth
              id="qtd-semestres"
              placeholder="6"
              defaultValue={formData?.qtdSemestres}
              label={
                <FormName
                  nome={
                    "Quantos semestres seu curso possui? (1 semestre = 6 meses) *"
                  }
                />
              }
              error={!!errors.qtdSemestres}
              helperText={errors.qtdSemestres?.message}
              inputProps={{
                type: "number",
                inputMode: "numeric",
                pattern: "[0-9]*",
                min: 2,
                max: 10,
              }}
              {...register("qtdSemestres")}
            />
          </Grid>

          <Grid item xs={12} lg={6}>
            <TextField
              fullWidth
              id="semestre-atual"
              placeholder="4"
              defaultValue={formData?.semestreAtual}
              label={
                <FormName
                  nome={"Qual seu semestre atual? (semestre de referência) *"}
                />
              }
              error={!!errors.semestreAtual || isGreaterThanTotalSemesters}
              helperText={
                errors.semestreAtual?.message ||
                (isGreaterThanTotalSemesters &&
                  "O semestre atual não pode ser maior que o valor de semestres totais do curso")
              }
              inputProps={{
                type: "number",
                inputMode: "numeric",
                pattern: "[0-9]*",
                min: 1,
                max: 10,
              }}
              {...register("semestreAtual", { valueAsNumber: true })}
              onWheel={(e) => (e.target as HTMLElement).blur()}
              onKeyDown={(e) =>
                ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()
              }
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <Box display="flex" flexDirection="column">
              <FormLabel
                id="label-comprovante-matricula"
                sx={{ color: errors.comprovanteMatricula ? "error.main" : "#525252" }}
              >
                  <FormName nome={"Adicionar comprovante de matricula (.pdf, até 1MB) *"} />
              </FormLabel>
              <Box display="flex" gap="1rem" alignItems="flex-end">
                {!comprovanteMatricula?.[0] && (
                  <Button
                    id="botao-comprovante"
                    variant="outlined"
                    color={errors.comprovanteMatricula ? "error" : "primary"}
                    component="label"
                  >
                    Adicionar
                    <input
                      id="s2-input-comprovante"
                      hidden
                      type="file"
                      accept="application/pdf"
                      {...register("comprovanteMatricula")}
                    />
                  </Button>
                )}
                {comprovanteMatricula?.[0] && (
                  <Button
                    id="botao-configuracoes"
                    variant="outlined"
                    color={errors.comprovanteMatricula ? "error" : "primary"}
                    component="label"
                  >
                    <UploadFileIcon sx={{ marginRight: "0.5rem" }} />
                    {comprovanteMatricula?.[0]?.name}
                    <input
                      id="s2-input-comprovanteMatricula"
                      accept=".pdf"
                      hidden
                      type="file"
                      {...register("comprovanteMatricula")}
                    />
                  </Button>
                )}
              </Box>
            </Box>
            {errors.comprovanteMatricula && (
              <FormHelperText error>
                {/* @ts-ignore */}
                {errors.comprovanteMatricula?.message}
              </FormHelperText>
            )}
          </Grid>

          <FormTitleSession title="Habilidades e Conhecimentos" margin={4} />
          <Grid item xs={12} lg={12}>
            <Tooltip
              title={
                "Linguagem de programação é um sistema formal de comunicação entre um programador e um computador"
              }
              placement="top"
              arrow
            >
              <FormControl fullWidth error={hasLang}>
                <InputLabel sx={{ color: "#525252" }} id="s2-select-linguagens">
                  <FormName
                    nome={
                      "Quais linguagens você tem aptidão: (Resposta não eliminatória) *"
                    }
                  />
                </InputLabel>
                <Select
                  labelId="s2-select-linguagens"
                  id="s2-select-linguagens-checkbox"
                  multiple
                  value={languages}
                  onChange={handleChange}
                  input={
                    <OutlinedInput
                      label={
                        "Quais linguagens você tem aptidão: (Resposta não eliminatória) *"
                      }
                    />
                  }
                  renderValue={(selected) => selected.join(", ")}
                  MenuProps={MenuProps}
                >
                  {names.map((name) => (
                    <MenuItem key={name} value={name}>
                      <Checkbox checked={languages.indexOf(name) > -1} />
                      <ListItemText
                        id={`s2-linguagens-${name}`}
                        primary={name}
                      />
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {hasLang ? "Pelo menos uma linguagem deve ser selecionada" : ""}
                </FormHelperText>
              </FormControl>
            </Tooltip>
          </Grid>
          <Grid item xs={12} lg={6}>
            <FormControl fullWidth error={!!errors.ingles}>
              <InputLabel sx={{ color: "#525252" }} id="ingles-label">
                <FormName nome={"Nível de Conhecimento em Inglês (Resposta não eliminatória) *"} />
              </InputLabel>
              <Select
                id="s2-nivel-ingles-candidato"
                labelId="ingles-label"
                label={<FormName nome={"Nível de Conhecimento em Inglês (Resposta não eliminatória) *"} />}
                value={watch("ingles") ?? initialSession.ingles ?? "NENHUM"}
                onChange={handleSelectChange("ingles")}
                error={!!errors.ingles}
                defaultValue={initialSession.ingles ?? "NENHUM"}
              >
                <MenuItem value="NENHUM">Nenhum</MenuItem>
                <MenuItem value="INICIANTE">Iniciante</MenuItem>
                <MenuItem value="INTERMEDIARIO">Intermediário</MenuItem>
                <MenuItem value="AVANCADO">Avançado</MenuItem>
                <MenuItem value="FLUENTE">Fluente</MenuItem>
              </Select>
              {errors.ingles && (
                <FormHelperText>{errors.ingles.message}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} lg={6}>
            <FormControl fullWidth error={!!errors.espanhol}>
              <InputLabel sx={{ color: "#525252" }} id="espanhol-label">
                <FormName nome={"Nível de Conhecimento em Espanhol (Resposta não eliminatória) *"} />
              </InputLabel>
              <Select
                label={<FormName nome={"Nível de Conhecimento em Espanhol (Resposta não eliminatória) *"} />}
                id="s2-nivel-espanhol-candidato"
                labelId="espanhol-label"
                value={watch("espanhol") ?? initialSession.espanhol ?? "NENHUM"}
                onChange={handleSelectChange("espanhol")}
                error={!!errors.espanhol}
                defaultValue={initialSession.espanhol ?? "NENHUM"}
              >
                <MenuItem value="NENHUM">Nenhum</MenuItem>
                <MenuItem value="INICIANTE">Iniciante</MenuItem>
                <MenuItem value="INTERMEDIARIO">Intermediário</MenuItem>
                <MenuItem value="AVANCADO">Avançado</MenuItem>
                <MenuItem value="FLUENTE">Fluente</MenuItem>
              </Select>
              {errors.espanhol && (
                <FormHelperText>{errors.espanhol.message}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormLabel sx={{ color: "#525252" }} component="legend">
              <FormName
                nome={
                  "Uma das nossas etapas eliminatórias de seleção será a realização de uma prova técnica. Será necessário conhecimento de lógica de programação e uso básico em algumas dessas tecnologias (Javascript, Java, PHP, C#, Python, C e C++), mas será avaliado principalmente raciocínio para solução de problemas. Tens conhecimento necessário para realizar esta prova específica?"
                }
              />
            </FormLabel>

            <Stack direction="row" spacing={2}>
              <FormLabel
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "#525252",
                }}
              >
                <RadioGroup
                  defaultValue={formData?.provaBoolean === true ? "T" : "F"}
                  row
                  sx={{ color: "#525252" }}
                >
                  <FormControlLabel
                    id="s2-candidato-prova-sim"
                    value="T"
                    control={<Radio />}
                    label="Sim"
                    {...register("provaBoolean")}
                  />
                  <FormControlLabel
                    id="s2-candidato-prova-nao"
                    value="F"
                    control={<Radio />}
                    label="Não"
                    {...register("provaBoolean")}
                  />
                </RadioGroup>
              </FormLabel>
            </Stack>
          </Grid>
          <Typography variant="caption" color="error">
            {errors.provaBoolean?.message}
          </Typography>
          <Grid item xs={12}>
            <FormLabel
              sx={{
                color: "#525252",
                marginBottom: "0.5rem",
              }}
              component="legend"
            >
              <FormName
                nome={
                  "Alguém te ensinou algo importante para a vida e que você nunca esqueceu? Quem foi e o que você aprendeu? *"
                }
              />
            </FormLabel>
            <TextField
              label={<FormName nome="Digite sua resposta" />}
              multiline
              defaultValue={formData?.algoimportante}
              minRows={4}
              type="textArea"
              size="medium"
              fullWidth
              inputProps={{ maxLength: 500 }}
              error={!!errors.algoimportante}
              helperText={helperWithCount(algoImportanteValue, 500, errors.algoimportante?.message, "algoimportante")}
              id="textarea-importante-candidato"
              {...register("algoimportante")}
              onFocus={() => setFocusedField("algoimportante")}
              onBlur={() => setFocusedField(null)}
            />
          </Grid>
          <FormTitleSession title="Interesse no Programa e na DBC" margin={4} />
          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.comoSoube}>
              <InputLabel sx={{ color: "#525252" }} id="como-soube-label">
                <FormName nome="Como você soube sobre o Vem Ser? *" />
              </InputLabel>
              <Select
                label={<FormName nome="Como você soube sobre o Vem Ser? *" />}
                id="s2-como-soube-sobre"
                labelId="como-soube-label"
                value={watch("comoSoube") ?? initialSession.comoSoube ?? ""}
                onChange={handleSelectChange("comoSoube")}
                error={!!errors.comoSoube}
              >
                <MenuItem value="Instagram">Instagram</MenuItem>
                <MenuItem value="Linkedin">Linkedin</MenuItem>
                <MenuItem value="Facebook">Facebook</MenuItem>
                <MenuItem value="Indicação">Indicação</MenuItem>
                <MenuItem value="Site/Google">Site/Google</MenuItem>
                <MenuItem value="TikTok">TikTok</MenuItem>
                <MenuItem value="Outro">Outro</MenuItem>
              </Select>
              {errors.comoSoube && (
                <FormHelperText>{errors.comoSoube.message}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} lg={12}>
            <FormLabel
              sx={{
                color: "#525252",
                marginTop: "0.5rem",
              }}
              component="legend"
            >
              <FormName
                nome={
                  "Como o programa é focado em Engenharia de Software, selecione até duas áreas da TI que mais te interessam."
                }
              />
            </FormLabel>
          </Grid>
          <Grid item xs={12} lg={6}>
            <FormControl fullWidth error={!!errors.trilha1}>
              <InputLabel sx={{ color: "#525252" }} id="trilha1-label">
                <FormName nome={"1ª opção de área de interesse em TI *"} />
              </InputLabel>
              <Controller
                name="trilha1"
                control={control}
                defaultValue={formData?.trilha1 ? formData.trilha1 : ""}
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="trilha1-label"
                    id="s2-trilha1"
                    label={<FormName nome={"1ª opção de área de interesse em TI *"} />}
                  >
                    {trilhas.map((trilha) => (
                      <MenuItem key={trilha.value} value={trilha.value}>
                        {trilha.nome}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors.trilha1 && (
                <FormHelperText>{errors.trilha1.message}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} lg={6}>
            <FormControl fullWidth error={!!errors.trilha2}>
              <InputLabel sx={{ color: "#525252" }} id="trilha2-label">
                <FormName nome={"2ª opção de área de interesse em TI *"} />
              </InputLabel>
              <Controller
                name="trilha2"
                control={control}
                defaultValue={formData?.trilha2 ? formData.trilha2 : ""}
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="trilha2-label"
                    id="s2-trilha2"
                    label={<FormName nome={"2ª opção de área de interesse em TI *"} />}
                    disabled={!watch("trilha1")}
                  >
                    {trilhas
                      .filter((trilha) => trilha.value !== watch("trilha1"))
                      .map((trilha) => (
                        <MenuItem key={trilha.value} value={trilha.value}>
                          {trilha.nome}
                        </MenuItem>
                      ))}
                  </Select>
                )}
              />
              {errors.trilha2 && (
                <FormHelperText>{errors.trilha2.message}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormLabel
              sx={{
                color: "#525252",
                marginBottom: "0.5rem",
              }}
              component="legend"
            >
              <FormName
                nome={
                  "Por qual motivo você se interessou pela área de tecnologia e pela DBC? *"
                }
              />
            </FormLabel>
            <TextField
              label={<FormName nome="Digite sua resposta" />}
              multiline
              defaultValue={formData?.resposta}
              minRows={4}
              type="textArea"
              size="medium"
              fullWidth
              inputProps={{ maxLength: 500 }}
              error={!!errors.resposta}
              helperText={helperWithCount(respostaValue, 500, errors.resposta?.message, "resposta")}
              id="s2-candidato-motivo"
              {...register("resposta")}
              onFocus={() => setFocusedField("resposta")}
              onBlur={() => setFocusedField(null)}
            />
          </Grid>
          <Grid item xs={12}>
            <FormLabel sx={{ color: "#525252" }} component="legend">
              <FormName
                nome={
                  "O estágio acontecerá de forma virtual, nos turnos da manhã e da tarde, das 09h às 12h e das 13h30 às 16h30, totalizando uma carga horária diária de 6 horas. Você tem disponibilidade para realizar o estágio e as atividades previstas? (Necessário computador, internet estável e webcam)"
                }
              />
            </FormLabel>

            <Stack direction="row" spacing={2}>
              <FormLabel
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "#525252",
                }}
              >
                <RadioGroup
                  row
                  sx={{ color: "#525252" }}
                  defaultValue={
                    formData?.disponibilidadeBoolean === true ? "T" : "F"
                  }
                >
                  <FormControlLabel
                    value="T"
                    control={<Radio />}
                    label="Sim"
                    id="s2-candidato-disponibilidade-sim"
                    {...register("disponibilidadeBoolean")}
                  />
                  <FormControlLabel
                    value="F"
                    control={<Radio />}
                    label="Não"
                    id="s2-candidato-disponibilidade-nao"
                    {...register("disponibilidadeBoolean")}
                  />
                </RadioGroup>
              </FormLabel>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <FormLabel sx={{ color: "#525252" }} component="legend">
              <FormName
                nome={
                  "Existe a possibilidade de efetivação para os profissionais que apresentarem bom desempenho ao longo do período de formação. Você teria interesse e disponibilidade para atuar em turno integral (manhã e tarde, até 44h semanais), caso surja essa possibilidade? (Considerando disponibilidade mínima de 1 ano para permanência na DBC)"
                }
              />
            </FormLabel>

            <Stack direction="row" spacing={2}>
              <FormLabel
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "#525252",
                }}
              >
                <RadioGroup
                  defaultValue={
                    formData?.efetivacaoBoolean === true ? "T" : "F"
                  }
                  row
                  sx={{ color: "#525252" }}
                >
                  <FormControlLabel
                    id="s2-candidato-efetivacao-sim"
                    value="T"
                    control={<Radio />}
                    label="Sim"
                    {...register("efetivacaoBoolean")}
                  />
                  <FormControlLabel
                    id="s2-candidato-efetivacao-nao"
                    value="F"
                    control={<Radio />}
                    label="Não"
                    {...register("efetivacaoBoolean")}
                  />
                </RadioGroup>
              </FormLabel>
            </Stack>
          </Grid>
          <FormTitleSession title="Informações Adicionais" margin={4} />
          <Grid item xs={12} md={6}>
            <TextField
              type="text"
              label={<FormName nome={"Qual o link do seu perfil no GitHub?"} />}
              defaultValue={
                formData?.github === "Nenhum" ? "" : formData?.github
              }
              placeholder="https://github.com/seuperfil"
              variant="outlined"
              sx={{ width: "100%" }}
              id="s2-candidato-github"
              InputProps={{
                startAdornment: (
                  <Box display="flex" alignItems="center" mr={1}>
                    <GitHubIcon sx={{ color: "primary.main" }} />
                  </Box>
                ),
              }}
              inputProps={{ maxLength: 255 }}
              error={!!errors.github}
              helperText={helperWithCount(githubValue, 255, errors.github?.message, "github")}
              {...register("github")}
              onFocus={() => setFocusedField("github")}
              onBlur={() => setFocusedField(null)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              type="text"
              label={<FormName nome={"Qual o link do seu Linkedin?"} />}
              defaultValue={
                formData?.linkedin !== "Nenhum" ? formData?.linkedin : ""
              }
              placeholder="https://www.linkedin.com/in/seuperfil/"
              variant="outlined"
              sx={{ width: "100%" }}
              id="s2-candidato-linkedin"
              InputProps={{
                startAdornment: (
                  <Box display="flex" alignItems="center" mr={1}>
                    <LinkedInIcon sx={{ color: "primary.main" }} />
                  </Box>
                ),
              }}
              inputProps={{ maxLength: 255 }}
              error={!!errors.linkedin}
              helperText={helperWithCount(linkedinValue, 255, errors.linkedin?.message, "linkedin")}
              {...register("linkedin")}
              onFocus={() => setFocusedField("linkedin")}
              onBlur={() => setFocusedField(null)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={
                "Especificações do seu computador (Resposta não eliminatória)*"
              }
              multiline
              placeholder={"Exemplo: Intel I5-8080, 16gbram, 240gb SSD."}
              minRows={2}
              type="textArea"
              size="medium"
              fullWidth
              inputProps={{ maxLength: 500 }}
              InputLabelProps={{ shrink: true }}
              error={!!errors.descConfigPc}
              helperText={helperWithCount(descConfigPcValue, 500, errors.descConfigPc?.message, "descConfigPc")}
              id="textarea-config-pc"
              {...register("descConfigPc")}
              onFocus={() => setFocusedField("descConfigPc")}
              onBlur={() => setFocusedField(null)}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <Box display="flex" flexDirection="column">
              <FormLabel
                id="label-curriculo-candidato"
                sx={{ color: errors.curriculo ? "error.main" : "#525252" }}
              >
                  <FormName nome={"Adicionar currículo (.pdf, até 1MB) *"} />
              </FormLabel>
              <Box display="flex" gap="1rem" alignItems="flex-end">
                {!curriculo?.[0] && (
                  <Button
                    id="botao-curriculo"
                    variant="outlined"
                    color={errors.curriculo ? "error" : "primary"}
                    component="label"
                  >
                    Adicionar
                    <input
                      id="s2-input-curriculo"
                      hidden
                      type="file"
                      accept="application/pdf"
                      {...register("curriculo")}
                    />
                  </Button>
                )}
                {curriculo?.[0] && (
                  <Button
                    id="botao-curriculo"
                    variant="outlined"
                    color={errors.curriculo ? "error" : "primary"}
                    component="label"
                  >
                    <UploadFileIcon sx={{ marginRight: "0.5rem" }} />
                    {curriculo?.[0]?.name}
                    <input
                      id="s2-input-curriculo-2"
                      hidden
                      type="file"
                      accept="application/pdf"
                      {...register("curriculo")}
                    />
                  </Button>
                )}
              </Box>
            </Box>
            {errors.curriculo && (
              <FormHelperText error>
                {/* @ts-ignore */}
                {errors.curriculo?.message}
              </FormHelperText>
            )}
          </Grid>
          <FormTitleSession title="Termos e Condições" margin={4} />
          <Grid item xs={12}>
            <FormGroup aria-label="position" row sx={{ color: "#525252" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!watch("lgpdBoolean")}
                    {...register("lgpdBoolean", { required: true })}
                    onChange={(e) => {
                      setValue("lgpdBoolean", e.target.checked, {
                        shouldValidate: true,
                      });
                      const current = watch();
                      const updated = {
                        ...current,
                        lgpdBoolean: e.target.checked,
                      };
                      saveToSession(updated, languages);
                    }}
                    id="s2-candidato-lgpd"
                  />
                }
                label={
                  <>
                    Li e concordo com a{" "}
                    <Link
                      href="https://www.dbccompany.com.br/politica-de-privacidade/"
                      target="_blank"
                      underline="hover"
                      marginLeft={"4px"}
                    >
                      Politica de privacidade DBC Company.
                    </Link>{" "}
                    *
                  </>
                }
              />
            </FormGroup>
            <Error id={"mensagem-erro-lgpd"} width={"100%"}>
              {errors.lgpdBoolean?.message}
            </Error>

            <FormGroup aria-label="position" row sx={{ color: "#525252" }}>
              <FormControlLabel
                control={
                  <Controller
                    name="polPrivacidade"
                    control={control}
                    rules={{
                      required:
                        "É necessário aceitar os termos de tratamento de dados e geolocalização para prosseguir",
                    }}
                    defaultValue={!!formData?.polPrivacidade}
                    render={({ field }) => (
                      <Checkbox
                        checked={!!field.value}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          dispatch(changeData({ polPrivacidade: e.target.checked }));
                        }}
                        id="s2-candidato-geolocalizacao"
                      />
                    )}
                  />
                }
                label="Concordo com o tratamento dos meus dados pessoais para fins de participação no processo seletivo. Estou ciente de que minha geolocalização será solicitada no envio final da inscrição, com a finalidade de garantir a autenticidade e a validade do meu consentimento, em conformidade com a LGPD. *"
              />
            </FormGroup>
            <Error id={"mensagem-erro-geolocalizacao"} width={"100%"}>
              {errors.polPrivacidade?.message}
            </Error>
          </Grid>

          {/* Card de Aviso / Alerta em Destaque */}
          <Grid item xs={12}>
            <Box
              sx={{
                backgroundColor: "#EBF3FF",
                borderLeft: "4px solid #1D4ED8",
                borderRadius: "8px",
                p: 2.5,
                mt: 1,
                mb: 1,
                display: "flex",
                gap: 2,
                alignItems: "flex-start",
              }}
            >
              <InfoOutlinedIcon sx={{ color: "#1D4ED8", mt: "2px", flexShrink: 0 }} />
              <Typography
                variant="body2"
                sx={{
                  color: "#1E40AF",
                  lineHeight: 1.6,
                  fontSize: "0.925rem",
                  "& strong": {
                    color: "#1E3A8A",
                    fontWeight: 700,
                  },
                }}
              >
                <strong>Atenção à próxima etapa:</strong> Após finalizar sua inscrição, você receberá um e-mail com o nosso questionário comportamental. <strong>O formulário deve ser respondido no prazo de até 8 horas após o envio (da inscrição) para darmos sequência ao seu processo seletivo.</strong>
              </Typography>
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              marginTop: "1.5rem",
            }}
          >
            <Button
              type="button"
              id="s2-botao-voltar"
              variant="outlined"
              onClick={() => {
                dispatch(previousStep());
              }}
              sx={{ width: "8rem" }}
            >
              Voltar
            </Button>
            <Button
              id="s2-botao-submit"
              variant="contained"
              type="submit"
              sx={{ width: "8rem" }}
              onClick={() => verificar()}
              disabled={
                Object.keys(errors).length !== 0 || isGreaterThanTotalSemesters
              }
            >
              Enviar
            </Button>
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        {matriculado !== "T" && (
          <Error id="erro-não-matriculado" width="100%">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.2rem",
                marginY: "0.2rem",
                fontSize: 14,
              }}
            >
              <ArrowCircleRight />
              Devido as restrições impostas pelas leis brasileiras, somente
              alunos que possuem vínculo com uma instituição de ensino podem se
              candidatar às vagas de estágio.
            </Box>
          </Error>
        )}
      </Grid>
    </FormGrid>
  );
};